import React from 'react';
import IssuesIcon from '@atlaskit/icon/glyph/issues';
import { Inline, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsIssueLinkDetailsPopupOpen } from '../../controllers/main';
import { IssueLinksDetailsPopupEntryPointContainer } from './issue-links-details-popup-entrypoint-container';
import { messages } from './messages';
import type { IssueLinksStatsButtonProps, IssueLinksStatsInlineProps } from './types';

// Replace with lodash/noop
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const isIssueLinkDetailsPopupStatsEnabled = (): Boolean => {
	const [expConfig] = UNSAFE_noExposureExp('jsw_link_issues_from_board_cards');
	return (
		// LD feature flag wrapper required to release to Release Track enrolled customers
		ff('jsw.perf.issue-links-on-board-cards') &&
		// Check if Milestone 3 for issue links details popup is enabled
		fg('jsw_enable_issue_links_popup_on_jsw_board_cards_m3') &&
		// check if the experiment is enabled
		expConfig.get('enableIssueLinkExperiment', false)
	);
};

const IssueLinksStatsInline = ({ issueLinkCountDisplay }: IssueLinksStatsInlineProps) => (
	<Inline space="space.050" alignBlock="center" xcss={iconButtonStyles}>
		<IssuesIcon
			size="small"
			label=""
			testId="issue-links-stats.ui.issue-links-stats-button.issues-icon"
		/>
		{issueLinkCountDisplay}
	</Inline>
);

export const IssueLinksStatsButton = ({
	issueLinkCount,
	issueKey,
	issueId,
	issueSummary,
	issueType,
	isFocusOnTrigger,
	feedbackCollectorPrefix,
	onIssueUnlink,
	onFocusToTrigger,
	canUserLinkIssue,
	withDueDate = false,
	maxIssueLinksStatsCount,
	sourceProductType,
	onSuccessfulIssueLink = noop,
	isLinkIssuePopupEnabled = false,
}: IssueLinksStatsButtonProps) => {
	const { formatMessage } = useIntl();
	const isOpen = useIsIssueLinkDetailsPopupOpen({
		openedIssueKey: issueKey,
	});

	const issueLinkCountDisplay =
		issueLinkCount > maxIssueLinksStatsCount ? `${maxIssueLinksStatsCount}+` : issueLinkCount;

	const tooltipContent =
		issueLinkCount > maxIssueLinksStatsCount
			? formatMessage(messages.genericMaxIssueLinksStatsTooltipLabel, {
					maxIssueLinksStatsCount,
				})
			: formatMessage(messages.issueLinksTooltipLabel, {
					issueLinkCount,
				});

	return (
		<Tooltip
			content={isOpen ? '' : tooltipContent}
			testId="issue-links-stats.ui.issue-links-stats-button.linked-issue-stats-tooltip"
			delay={0}
		>
			{isIssueLinkDetailsPopupStatsEnabled() ? (
				<IssueLinksDetailsPopupEntryPointContainer
					isOpen={isOpen}
					isFocusOnTrigger={isFocusOnTrigger}
					issueId={issueId}
					issueLinkCount={issueLinkCountDisplay}
					issueSummary={issueSummary}
					issueType={issueType}
					issueKey={issueKey}
					feedbackCollectorPrefix={feedbackCollectorPrefix}
					onIssueUnlink={onIssueUnlink}
					onFocusToTrigger={onFocusToTrigger}
					canUserLinkIssue={canUserLinkIssue}
					iconLabel={tooltipContent}
					withDueDate={withDueDate}
					sourceProductType={sourceProductType}
					onSuccessfulIssueLink={onSuccessfulIssueLink}
					isLinkIssuePopupEnabled={isLinkIssuePopupEnabled}
				/>
			) : (
				<IssueLinksStatsInline issueLinkCountDisplay={issueLinkCountDisplay} />
			)}
		</Tooltip>
	);
};

const iconButtonStyles = xcss({
	paddingLeft: 'space.050',
	paddingRight: 'space.100',
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
});
